// src/pages/trading-portal/AIAnalysisEngine/VolatilityPredictionPage.js
// Volatility Prediction - single page: Add Config -> auto pipeline (realized vol + forecast + ensemble)

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Alert,
    Button,
    TextField,
    Table,
    TableContainer,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TablePagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Chip,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Autocomplete,
    Skeleton,
    Tooltip,
    Collapse,
    LinearProgress,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
    ShowChart as ShowChartIcon,
    PlayArrow as PlayArrowIcon,
    PlaylistPlay as PlaylistPlayIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import axiosInstance from '../../../utils/axiosInstance';
import { hasPermission } from '../../../utils/permissionUtils';
import {
    VolModelType,
    VolModelTypeLabels,
    VolModelTypeColors,
    VolForecastHorizon,
    VolForecastHorizonLabels,
    VolForecastStatus,
    VolForecastStatusLabels,
    VolForecastStatusColors,
    EnsembleWeightMethodLabels,
} from '../stockTradingEnums';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#f59e0b';

const VolatilityPredictionPage = () => {
    // Permissions
    const [canView, setCanView] = useState(false);
    const [canAdd, setCanAdd] = useState(false);
    const [canEdit, setCanEdit] = useState(false);
    const [canDelete, setCanDelete] = useState(false);

    // Symbols
    const [symbols, setSymbols] = useState([]);

    // Global
    const [error, setError] = useState(null);
    const [successMsg, setSuccessMsg] = useState(null);

    // Summary data (configs + latest forecast/rv/ensemble)
    const [summaryData, setSummaryData] = useState([]);
    const [summaryLoading, setSummaryLoading] = useState(true);
    const [summaryPage, setSummaryPage] = useState(0);
    const [summaryRowsPerPage, setSummaryRowsPerPage] = useState(10);
    const [summaryTotalCount, setSummaryTotalCount] = useState(0);

    // Pipeline running state
    const [runningPipeline, setRunningPipeline] = useState({}); // { configId: true }
    const [runningAll, setRunningAll] = useState(false);

    // Expanded rows for details
    const [expandedRow, setExpandedRow] = useState(null);

    // Config dialog
    const [configDialogOpen, setConfigDialogOpen] = useState(false);
    const [configEditMode, setConfigEditMode] = useState(false);
    const [selectedConfig, setSelectedConfig] = useState(null);
    const [configFormData, setConfigFormData] = useState({
        symbol_id: null,
        model_type: '',
        horizon: '',
        lookback_days: 252,
        retrain_frequency_hours: 24,
        is_active: true,
    });
    const [configFormErrors, setConfigFormErrors] = useState({});
    const [configSubmitting, setConfigSubmitting] = useState(false);

    // Config delete
    const [configDeleteDialogOpen, setConfigDeleteDialogOpen] = useState(false);
    const [configToDelete, setConfigToDelete] = useState(null);

    // ========== Permissions ==========
    useEffect(() => {
        const checkPermissions = async () => {
            const view = await hasPermission('tr-aae-vol-prediction', 'view');
            const add = await hasPermission('tr-aae-vol-prediction', 'add');
            const edit = await hasPermission('tr-aae-vol-prediction', 'edit');
            const del = await hasPermission('tr-aae-vol-prediction', 'delete');
            setCanView(view);
            setCanAdd(add);
            setCanEdit(edit);
            setCanDelete(del);
        };
        checkPermissions();
    }, []);

    // ========== Fetch Symbols ==========
    const fetchSymbols = useCallback(async () => {
        try {
            const response = await axiosInstance.get('/api/stock-trading/symbols?per_page=200');
            if (response.data.success) {
                setSymbols(response.data.data || []);
            }
        } catch (err) {
            console.error('Failed to fetch symbols:', err);
        }
    }, []);

    useEffect(() => {
        if (canView) fetchSymbols();
    }, [fetchSymbols, canView]);

    // ========== Fetch Summary ==========
    const fetchSummary = useCallback(async () => {
        if (!canView) return;
        setSummaryLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page: summaryPage + 1, per_page: summaryRowsPerPage });
            const response = await axiosInstance.get(`/api/volatility-prediction/summary?${params.toString()}`);
            if (response.data.success) {
                setSummaryData(response.data.data || []);
                setSummaryTotalCount(response.data.pagination?.total || 0);
            } else {
                setError(response.data.message || 'Failed to fetch summary');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch summary');
        } finally {
            setSummaryLoading(false);
        }
    }, [canView, summaryPage, summaryRowsPerPage]);

    useEffect(() => {
        if (canView) fetchSummary();
    }, [fetchSummary, canView]);

    // ========== Config Handlers ==========
    const resetConfigForm = () => {
        setConfigFormData({ symbol_id: null, model_type: '', horizon: '', lookback_days: 252, retrain_frequency_hours: 24, is_active: true });
        setConfigFormErrors({});
    };

    const handleConfigCreateOpen = () => {
        resetConfigForm();
        setConfigEditMode(false);
        setSelectedConfig(null);
        setConfigDialogOpen(true);
    };

    const handleConfigEditOpen = (config) => {
        setSelectedConfig(config);
        setConfigEditMode(true);
        const sym = symbols.find(s => s.id === config.symbol_id) || null;
        setConfigFormData({
            symbol_id: sym,
            model_type: config.model_type || '',
            horizon: config.horizon || '',
            lookback_days: config.parameters_json?.lookback_days || 252,
            retrain_frequency_hours: config.parameters_json?.retrain_frequency_hours || 24,
            is_active: config.is_active !== false,
        });
        setConfigFormErrors({});
        setConfigDialogOpen(true);
    };

    const handleConfigDialogClose = () => {
        setConfigDialogOpen(false);
        resetConfigForm();
        setSelectedConfig(null);
    };

    const handleConfigFormChange = (field) => (event) => {
        const value = field === 'is_active' ? event.target.value === 'true' : event.target.value;
        setConfigFormData(prev => ({ ...prev, [field]: value }));
        if (configFormErrors[field]) setConfigFormErrors(prev => ({ ...prev, [field]: null }));
    };

    const validateConfigForm = () => {
        const errors = {};
        if (!configFormData.symbol_id) errors.symbol_id = 'Symbol is required';
        if (!configFormData.model_type) errors.model_type = 'Model type is required';
        if (!configFormData.horizon) errors.horizon = 'Horizon is required';
        setConfigFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleConfigSubmit = async () => {
        if (!validateConfigForm()) return;
        setConfigSubmitting(true);
        try {
            const payload = {
                symbol_id: configFormData.symbol_id?.id || configFormData.symbol_id,
                model_type: configFormData.model_type,
                horizon: configFormData.horizon,
                lookback_days: parseInt(configFormData.lookback_days, 10),
                retrain_frequency_hours: parseInt(configFormData.retrain_frequency_hours, 10),
                is_active: configFormData.is_active,
            };
            let response;
            if (configEditMode && selectedConfig) {
                response = await axiosInstance.put(`/api/volatility-prediction/configs/${selectedConfig.id}`, payload);
            } else {
                response = await axiosInstance.post('/api/volatility-prediction/configs', payload);
            }
            if (response.data.success) {
                handleConfigDialogClose();
                fetchSummary();
            } else {
                setConfigFormErrors({ submit: response.data.message || 'Failed to save config' });
            }
        } catch (err) {
            setConfigFormErrors({ submit: err.response?.data?.message || 'Failed to save config' });
        } finally {
            setConfigSubmitting(false);
        }
    };

    const handleConfigDeleteClick = (config) => { setConfigToDelete(config); setConfigDeleteDialogOpen(true); };

    const handleConfigDeleteConfirm = async () => {
        if (!configToDelete) return;
        try {
            const response = await axiosInstance.delete(`/api/volatility-prediction/configs/${configToDelete.id}`);
            if (response.data.success) {
                fetchSummary();
                setConfigDeleteDialogOpen(false);
                setConfigToDelete(null);
            } else {
                setError(response.data.message || 'Failed to delete config');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete config');
        }
    };

    // ========== Pipeline Handlers ==========
    const handleRunPipeline = async (configId) => {
        setRunningPipeline(prev => ({ ...prev, [configId]: true }));
        setError(null);
        try {
            const response = await axiosInstance.post(`/api/volatility-prediction/pipeline/${configId}`);
            if (response.data.success) {
                setSuccessMsg(response.data.message);
                setTimeout(() => setSuccessMsg(null), 5000);
                fetchSummary();
            } else {
                setError(response.data.message || 'Pipeline failed');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Pipeline failed');
        } finally {
            setRunningPipeline(prev => ({ ...prev, [configId]: false }));
        }
    };

    const handleRunAllPipelines = async () => {
        setRunningAll(true);
        setError(null);
        try {
            const response = await axiosInstance.post('/api/volatility-prediction/pipeline/run-all');
            if (response.data.success) {
                setSuccessMsg(response.data.message);
                setTimeout(() => setSuccessMsg(null), 5000);
                fetchSummary();
            } else {
                setError(response.data.message || 'Pipeline failed');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Pipeline failed');
        } finally {
            setRunningAll(false);
        }
    };

    // ========== Permission Guard ==========
    if (!canView) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">You do not have permission to view Volatility Prediction.</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Gradient Header */}
            <Paper
                elevation={0}
                sx={{
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    borderRadius: '16px',
                    px: { xs: 2.5, md: 4 },
                    py: { xs: 3, md: 4 },
                    mb: 3,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'absolute', bottom: -40, right: 80, width: 100, height: 100, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.04)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <ShowChartIcon sx={{ fontSize: 28, color: '#fff' }} />
                    </Box>
                    <Box>
                        <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' }, lineHeight: 1.2 }}>
                            Volatility Prediction
                        </Typography>
                        <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>
                            Add a config and run the full pipeline: Realized Vol, Forecast, and Ensemble
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            {/* Alerts */}
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
            {successMsg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg(null)}>{successMsg}</Alert>}

            {/* Action Bar */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                    Volatility Configs & Results
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button startIcon={<RefreshIcon />} onClick={fetchSummary} size="small" sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}>
                        Refresh
                    </Button>
                    {canAdd && (
                        <>
                            <Button
                                startIcon={runningAll ? <CircularProgress size={16} /> : <PlaylistPlayIcon />}
                                variant="outlined" onClick={handleRunAllPipelines} disabled={runningAll} size="small"
                                sx={{ fontFamily, textTransform: 'none', fontWeight: 600, borderColor: ACCENT, color: ACCENT, '&:hover': { borderColor: '#d97706', bgcolor: '#fffbeb' } }}>
                                Run All Pipelines
                            </Button>
                            <Button startIcon={<AddIcon />} variant="contained" onClick={handleConfigCreateOpen} size="small"
                                sx={{ fontFamily, textTransform: 'none', fontWeight: 600, bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' } }}>
                                Add Config
                            </Button>
                        </>
                    )}
                </Box>
            </Box>

            {/* Main Table */}
            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
                {summaryLoading ? (
                    <Box sx={{ p: 3 }}>{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} variant="text" sx={{ fontSize: '1.2rem', mb: 1 }} />)}</Box>
                ) : (
                    <>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem', width: 40 }} />
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Symbol</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Model</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Horizon</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Forecast</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Actual (RV)</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Ensemble</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Status</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }} align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {summaryData.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                                                <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>
                                                    No configs found. Click "Add Config" to get started.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : summaryData.map((row) => {
                                        const config = row.config;
                                        const forecast = row.latest_forecast;
                                        const rv = row.latest_realized_vol;
                                        const ensemble = row.latest_ensemble;
                                        const isExpanded = expandedRow === config.id;
                                        const isPipelineRunning = runningPipeline[config.id];

                                        return (
                                            <React.Fragment key={config.id}>
                                                <TableRow sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                                                    {/* Expand */}
                                                    <TableCell sx={{ p: 0.5 }}>
                                                        <IconButton size="small" onClick={() => setExpandedRow(isExpanded ? null : config.id)}>
                                                            {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                                        </IconButton>
                                                    </TableCell>
                                                    {/* Symbol */}
                                                    <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem' }}>
                                                        {config.symbol || '--'}
                                                    </TableCell>
                                                    {/* Model */}
                                                    <TableCell>
                                                        <Chip label={VolModelTypeLabels[config.model_type] || config.model_type} size="small"
                                                            color={VolModelTypeColors[config.model_type] || 'default'}
                                                            sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }} />
                                                    </TableCell>
                                                    {/* Horizon */}
                                                    <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.8rem' }}>
                                                        {VolForecastHorizonLabels[config.horizon] || config.horizon || '--'}
                                                    </TableCell>
                                                    {/* Forecast Value */}
                                                    <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem' }}>
                                                        {forecast?.forecast_value != null ? `${(forecast.forecast_value * 100).toFixed(2)}%` : '--'}
                                                    </TableCell>
                                                    {/* Actual (Realized Vol) */}
                                                    <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.8rem' }}>
                                                        {rv?.annualized_value != null ? `${(rv.annualized_value * 100).toFixed(2)}%` : '--'}
                                                    </TableCell>
                                                    {/* Ensemble */}
                                                    <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem' }}>
                                                        {ensemble?.ensemble_forecast != null ? `${(ensemble.ensemble_forecast * 100).toFixed(2)}%` : '--'}
                                                    </TableCell>
                                                    {/* Status */}
                                                    <TableCell>
                                                        {forecast ? (
                                                            <Chip label={VolForecastStatusLabels[forecast.status] || forecast.status || 'N/A'} size="small"
                                                                color={VolForecastStatusColors[forecast.status] || 'default'}
                                                                sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }} />
                                                        ) : (
                                                            <Chip label="Not Run" size="small" color="default"
                                                                sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }} />
                                                        )}
                                                    </TableCell>
                                                    {/* Actions */}
                                                    <TableCell align="right">
                                                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                                            {canAdd && (
                                                                <Tooltip title="Run Pipeline (RV + Forecast + Ensemble)">
                                                                    <IconButton size="small" onClick={() => handleRunPipeline(config.id)}
                                                                        disabled={isPipelineRunning || runningAll}
                                                                        sx={{ color: ACCENT }}>
                                                                        {isPipelineRunning ? <CircularProgress size={16} /> : <PlayArrowIcon fontSize="small" />}
                                                                    </IconButton>
                                                                </Tooltip>
                                                            )}
                                                            {canEdit && (
                                                                <Tooltip title="Edit Config">
                                                                    <IconButton size="small" onClick={() => handleConfigEditOpen(config)} sx={{ color: '#64748b' }}>
                                                                        <EditIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            )}
                                                            {canDelete && (
                                                                <Tooltip title="Delete Config">
                                                                    <IconButton size="small" onClick={() => handleConfigDeleteClick(config)} sx={{ color: '#ef4444' }}>
                                                                        <DeleteIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>

                                                {/* Expanded Details Row */}
                                                <TableRow>
                                                    <TableCell colSpan={9} sx={{ p: 0, borderBottom: isExpanded ? '1px solid #e2e8f0' : 'none' }}>
                                                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                            <Box sx={{ p: 2, bgcolor: '#f8fafc' }}>
                                                                <Grid container spacing={2}>
                                                                    {/* Config Details */}
                                                                    <Grid item xs={12} md={3}>
                                                                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.8rem', color: '#475569', mb: 1 }}>Config</Typography>
                                                                        <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b' }}>
                                                                            Lookback: {config.parameters_json?.lookback_days || 252} days
                                                                        </Typography>
                                                                        <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b' }}>
                                                                            Retrain: {config.parameters_json?.retrain_frequency_hours || 24} hrs
                                                                        </Typography>
                                                                        <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b' }}>
                                                                            Active: {config.is_active ? 'Yes' : 'No'}
                                                                        </Typography>
                                                                    </Grid>

                                                                    {/* Forecast Details */}
                                                                    <Grid item xs={12} md={3}>
                                                                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.8rem', color: '#475569', mb: 1 }}>Forecast</Typography>
                                                                        {forecast ? (
                                                                            <>
                                                                                <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b' }}>
                                                                                    Value: {forecast.forecast_value != null ? `${(forecast.forecast_value * 100).toFixed(4)}%` : '--'}
                                                                                </Typography>
                                                                                <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b' }}>
                                                                                    Confidence: [{forecast.confidence_lower != null ? (forecast.confidence_lower * 100).toFixed(2) : '--'}%, {forecast.confidence_upper != null ? (forecast.confidence_upper * 100).toFixed(2) : '--'}%]
                                                                                </Typography>
                                                                                <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b' }}>
                                                                                    Date: {forecast.forecast_date || '--'}
                                                                                </Typography>
                                                                            </>
                                                                        ) : (
                                                                            <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8' }}>Not run yet</Typography>
                                                                        )}
                                                                    </Grid>

                                                                    {/* Realized Vol Details */}
                                                                    <Grid item xs={12} md={3}>
                                                                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.8rem', color: '#475569', mb: 1 }}>Realized Vol</Typography>
                                                                        {rv ? (
                                                                            <>
                                                                                <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b' }}>
                                                                                    Daily: {rv.value != null ? `${(rv.value * 100).toFixed(4)}%` : '--'}
                                                                                </Typography>
                                                                                <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b' }}>
                                                                                    Annualized: {rv.annualized_value != null ? `${(rv.annualized_value * 100).toFixed(2)}%` : '--'}
                                                                                </Typography>
                                                                                <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b' }}>
                                                                                    Window: {rv.window_days} days | {rv.estimator_type}
                                                                                </Typography>
                                                                                <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b' }}>
                                                                                    Date: {rv.calculation_date || '--'}
                                                                                </Typography>
                                                                            </>
                                                                        ) : (
                                                                            <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8' }}>Not calculated yet</Typography>
                                                                        )}
                                                                    </Grid>

                                                                    {/* Ensemble Details */}
                                                                    <Grid item xs={12} md={3}>
                                                                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.8rem', color: '#475569', mb: 1 }}>Ensemble</Typography>
                                                                        {ensemble ? (
                                                                            <>
                                                                                <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b' }}>
                                                                                    Value: {ensemble.ensemble_forecast != null ? `${(ensemble.ensemble_forecast * 100).toFixed(4)}%` : '--'}
                                                                                </Typography>
                                                                                <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b' }}>
                                                                                    Method: {EnsembleWeightMethodLabels[ensemble.weight_method] || ensemble.weight_method || '--'}
                                                                                </Typography>
                                                                                {ensemble.component_weights_json && (
                                                                                    <Box sx={{ mt: 0.5 }}>
                                                                                        {Object.entries(
                                                                                            typeof ensemble.component_weights_json === 'string'
                                                                                                ? JSON.parse(ensemble.component_weights_json)
                                                                                                : ensemble.component_weights_json
                                                                                        ).map(([model, weight]) => (
                                                                                            <Box key={model} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                                                                                                <Typography sx={{ fontFamily, fontSize: '0.65rem', color: '#64748b', minWidth: 70 }}>
                                                                                                    {VolModelTypeLabels[model] || model}
                                                                                                </Typography>
                                                                                                <LinearProgress
                                                                                                    variant="determinate"
                                                                                                    value={weight * 100}
                                                                                                    sx={{ flex: 1, height: 4, borderRadius: 2, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: ACCENT } }}
                                                                                                />
                                                                                                <Typography sx={{ fontFamily, fontSize: '0.65rem', fontWeight: 600, color: '#1e293b', minWidth: 30 }}>
                                                                                                    {(weight * 100).toFixed(0)}%
                                                                                                </Typography>
                                                                                            </Box>
                                                                                        ))}
                                                                                    </Box>
                                                                                )}
                                                                                {ensemble.ai_recommendation && (
                                                                                    <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#475569', mt: 0.5, fontStyle: 'italic' }}>
                                                                                        {ensemble.ai_recommendation}
                                                                                    </Typography>
                                                                                )}
                                                                            </>
                                                                        ) : (
                                                                            <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8' }}>
                                                                                Needs 2+ model forecasts for this symbol
                                                                            </Typography>
                                                                        )}
                                                                    </Grid>
                                                                </Grid>
                                                            </Box>
                                                        </Collapse>
                                                    </TableCell>
                                                </TableRow>
                                            </React.Fragment>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={summaryTotalCount}
                            page={summaryPage}
                            onPageChange={(e, newPage) => setSummaryPage(newPage)}
                            rowsPerPage={summaryRowsPerPage}
                            onRowsPerPageChange={(e) => { setSummaryRowsPerPage(parseInt(e.target.value, 10)); setSummaryPage(0); }}
                            rowsPerPageOptions={[5, 10, 25]}
                            sx={{ '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontFamily } }}
                        />
                    </>
                )}
            </Paper>

            {/* ========== Config Add/Edit Dialog ========== */}
            <Dialog open={configDialogOpen} onClose={handleConfigDialogClose} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>{configEditMode ? 'Edit Forecast Config' : 'Add Forecast Config'}</DialogTitle>
                <DialogContent>
                    {configFormErrors.submit && <Alert severity="error" sx={{ mb: 2 }}>{configFormErrors.submit}</Alert>}
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <Autocomplete
                                options={symbols}
                                getOptionLabel={(option) => `${option.symbol} - ${option.company_name || ''}`}
                                value={configFormData.symbol_id}
                                onChange={(e, newValue) => {
                                    setConfigFormData(prev => ({ ...prev, symbol_id: newValue }));
                                    if (configFormErrors.symbol_id) setConfigFormErrors(prev => ({ ...prev, symbol_id: null }));
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} label="Symbol" error={!!configFormErrors.symbol_id} helperText={configFormErrors.symbol_id} required />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth error={!!configFormErrors.model_type} required>
                                <InputLabel>Model Type</InputLabel>
                                <Select value={configFormData.model_type} onChange={handleConfigFormChange('model_type')} label="Model Type">
                                    {Object.entries(VolModelType).map(([key, value]) => (
                                        <MenuItem key={key} value={value}>{VolModelTypeLabels[key]}</MenuItem>
                                    ))}
                                </Select>
                                {configFormErrors.model_type && <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>{configFormErrors.model_type}</Typography>}
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth error={!!configFormErrors.horizon} required>
                                <InputLabel>Horizon</InputLabel>
                                <Select value={configFormData.horizon} onChange={handleConfigFormChange('horizon')} label="Horizon">
                                    {Object.entries(VolForecastHorizon).map(([key, value]) => (
                                        <MenuItem key={key} value={value}>{VolForecastHorizonLabels[key]}</MenuItem>
                                    ))}
                                </Select>
                                {configFormErrors.horizon && <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>{configFormErrors.horizon}</Typography>}
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Lookback Days" type="number" value={configFormData.lookback_days}
                                onChange={(e) => setConfigFormData(prev => ({ ...prev, lookback_days: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Retrain Frequency (hours)" type="number" value={configFormData.retrain_frequency_hours}
                                onChange={(e) => setConfigFormData(prev => ({ ...prev, retrain_frequency_hours: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>Status</InputLabel>
                                <Select value={configFormData.is_active.toString()} onChange={handleConfigFormChange('is_active')} label="Status">
                                    <MenuItem value="true">Active</MenuItem>
                                    <MenuItem value="false">Inactive</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={handleConfigDialogClose} sx={{ fontFamily, textTransform: 'none', fontWeight: 600, color: '#64748b' }}>Cancel</Button>
                    <Button onClick={handleConfigSubmit} variant="contained" disabled={configSubmitting}
                        sx={{ fontFamily, textTransform: 'none', fontWeight: 600, bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' } }}>
                        {configSubmitting ? <CircularProgress size={20} /> : (configEditMode ? 'Update' : 'Create')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ========== Config Delete Dialog ========== */}
            <Dialog open={configDeleteDialogOpen} onClose={() => { setConfigDeleteDialogOpen(false); setConfigToDelete(null); }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Delete Forecast Config</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily }}>
                        Are you sure you want to delete this forecast configuration? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => { setConfigDeleteDialogOpen(false); setConfigToDelete(null); }} sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}>Cancel</Button>
                    <Button onClick={handleConfigDeleteConfirm} variant="contained" color="error" sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}>Delete</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default VolatilityPredictionPage;
